import { styled } from "@mui/material";
import * as Yup from "yup";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import apiCall from "../../../../utils/apiCall";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { NotificationContext } from "../../../../providers/notification";

interface CreateIndustryPayload {
    IndustryId: number;
    IsActive:boolean;
    IndustrySector: string;
    IndustryName:string;
}

interface IndustryTypeData{
    Name: number;
    IndustryTypeId: string;
}

interface ComponentProps {
    handleClose: () => void;
}

const CreateIndustryContent: React.FC<ComponentProps> = ({ handleClose }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);

    const GetAllIndustryTypes = async (): Promise<any> => {
        return await apiCall().get(`/SetUp/GetAllIndustryTypes`);
    }

    const { data, isLoading, isError, error } = useQuery(['get-Industrytype'], GetAllIndustryTypes, {
		refetchOnWindowFocus: false,
		retry: 1,
	});
    
    const IndustryTypeOption =
    data?.data?.Data?.length > 0 ? (
        data?.data?.Data.map((Industrytypedata : IndustryTypeData ) => (
                <option value={Industrytypedata?.IndustryTypeId} key = {Industrytypedata?.IndustryTypeId} >
                    {Industrytypedata?.Name}
                </option>
            ))
        ) : (
            <option disabled>
                {isLoading ? 'loading....' : isError ? 'error': ''}
            </option>
    )

  const createIndustry = async (payload: CreateIndustryPayload) => {
    return await apiCall().post(
      '/SetUp/CreateUpdateIndustry',
      payload
    );
  };
  const createIndustryMutation = useMutation(
    ["createIndustry"],
    createIndustry,
    { retry: 0 }
  );

  const createIndustryHandler = async (
    values: FormikValues,
    resetForm: () => void
  ) => {
    const createIndustryPayload: CreateIndustryPayload = {
        IndustryId: 0,
        IndustryName: values.IndustryName,
        IndustrySector: values.IndustrySector,
        IsActive: false,
    };

    try {
      await createIndustryMutation.mutateAsync(createIndustryPayload, {
        onSuccess: () => {    
            showNotification({
                message: 'Industry created successful',
                type: 'success',
            });
            queryClient.refetchQueries(['get-AllIndustry']);
            handleClose();
        },
      });
      
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error?.message,
        type: "error",
      });
    }
  };

    const validationSchema = Yup.object().shape({
        IndustryName: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('Industry is required'),
        IndustrySector:Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('Industry Type is required'),
        
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            IndustryName: '',
            IndustrySector:''
        },
        onSubmit: (values, { resetForm }) => {
            createIndustryHandler(values, resetForm);
        },
        validationSchema,
    });
   
    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='IndustryName'
                        type='text'
                        as={Input}
                        label='Industry Name'
                    
                        placeholder='Enter Industry Name'
                    />
                    <Field 
                        name='IndustrySector'
                        type='text'
                        as={Input}
                        label='Industry Sector'
                        placeholder='Enter Industry Sector'
                    />
                     {/* <Field 
                        name='description'
                        type='text'
                        as={CustomTextArea}
                        label='Description'
                        placeholder='Enter Description'
                    /> */}
                                       
                </FormWrapper>
              
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton text='Cancel' onClick={handleClose}/>
                        <StyledButton 
                            disabled={createIndustryMutation.isLoading}  
                            text={createIndustryMutation.isLoading ? "Submitting..." : "Submit"}
                            onClick={formik.handleSubmit} 
                            type="submit"
                        />
                    </ButtonContainer>
                </ButtonWrapper>
              
            </form>
        </FormikProvider>
    )
};


export default CreateIndustryContent;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

